// @ts-nocheck
import React from 'react';
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BarChart3, TrendingUp, MessageSquare } from "lucide-react";
import { useFeedbackAnalytics } from "@/hooks/api-hooks";

export function TotalFeedbackPage() {
  const { data: analyticsData, isLoading } = useFeedbackAnalytics();
  const analytics = analyticsData?.data || null;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading feedback statistics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/feedback"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Feedback
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl flex items-center justify-center">
              <BarChart3 className="h-8 w-8 text-slate-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Total Feedback</h1>
              <p className="text-muted-foreground text-lg">
                Complete overview of all your feedback activity
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl flex items-center justify-center">
                  <BarChart3 className="h-6 w-6 text-slate-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {(analytics?.metrics?.totalFeedbackReceived || 0) + (analytics?.metrics?.totalFeedbackGiven || 0)}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Feedback</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Link to="/feedback/total/received">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      {analytics?.metrics?.totalFeedbackReceived || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Feedback Received</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/feedback/total/given">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl flex items-center justify-center">
                    <MessageSquare className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">
                      {analytics?.metrics?.totalFeedbackGiven || 0}
                    </div>
                    <div className="text-sm text-muted-foreground">Feedback Given</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Quick Navigation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Link to="/feedback/total/received">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  Feedback Received
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  View all feedback you've received from colleagues and team members.
                </p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/feedback/total/given">
            <Card className="hover:shadow-md transition-shadow cursor-pointer">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-purple-600" />
                  </div>
                  Feedback Given
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  View all feedback you've provided to colleagues and team members.
                </p>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Overview Stats */}
        <Card>
          <CardHeader>
            <CardTitle>Feedback Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">
                  {analytics?.metrics?.overallRating || '0'}
                </div>
                <div className="text-sm text-muted-foreground">Average Rating</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">
                  {analytics?.metrics?.contributors || '0'}
                </div>
                <div className="text-sm text-muted-foreground">Contributors</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">
                  {analytics?.metrics?.pendingReviews || '0'}
                </div>
                <div className="text-sm text-muted-foreground">Pending Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground mb-1">
                  {analytics?.metrics?.totalReviews || '0'}
                </div>
                <div className="text-sm text-muted-foreground">Total Reviews</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default TotalFeedbackPage
